import {useState} from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import Contact from './../assets/Svgs/email.svg';
import AlertModal from "./AlertModal";

const ContactCard = () => {

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('Required'),
      lastName: Yup.string().required('Required'),
      email: Yup.string().email('Invalid email').required('Required'),
      phone: Yup.string().required('Required'),
      subject: Yup.string().required('Required'),
      message: Yup.string().required('Required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      // Read submitted emails from localStorage
      const submittedEmails = JSON.parse(localStorage.getItem("submittedEmails")) || [];

      // Rule 1: Prevent duplicate submission from the same email
      if (submittedEmails.includes(values.email)) {
        setAlertMessage("This email has already submitted the form. Only one submission is allowed.");
        setShowAlert(true);
        resetForm();
        return;
      }
      // Rule 2: Prevent more than one submission from this browser (any email)
      if (submittedEmails.length!==0) {
        setAlertMessage("You have already sent a message. Only one submission per user is allowed.");
        setShowAlert(true);
        resetForm();
        return;
      }
      

      // Submit to FormSubmit
      await fetch("https://formsubmit.co/ajax/98eac2432274357d2ba5fe586abe5fbc", {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(values),
      });

      // Store the email in localStorage
      submittedEmails.push(values.email);
      localStorage.setItem("submittedEmails", JSON.stringify(submittedEmails));

      resetForm();
      setAlertMessage("Message sent successfully!");
      setShowAlert(true);
        
    }
  });

  return (
    <div className="bg-emerald-500 bg-opacity-10 py-12 px-4 md:px-20">
      <div className="flex flex-col md:flex-row items-start justify-between gap-10">
        
        {/* Left Section */}
        <div className="flex-1">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Contact Our Legal Team Today
          </h2>
          <p className="text-gray-700 mb-6">
            When reaching out, please include your full name and contact number so our legal team can respond promptly and efficiently.
          </p>
          <ul className="space-y-4 text-gray-700">
            <li className="flex items-center gap-3">
              <FontAwesomeIcon icon={faEnvelope} className="text-emerald-500 text-lg" />
              legal@pocketlpo.com
            </li>
            <li className="flex items-center gap-3">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-emerald-500 text-lg" />
              Available 7 days a week, 9am–7pm
            </li>
          </ul>
          <div className="hidden md:flex justify-center mt-10">
            <img
              src={Contact}
              alt="Contact"
              className="md:w-64 h-auto rounded-t-3xl rounded-b-lg animate-swing mx-auto"
            />
          </div>
        </div>

        {/* Right Section (Formik Form) */}
        <div className="flex-1 bg-white rounded-lg shadow-md p-8 w-full">
          <h3 className="text-xl font-bold mb-6">Request a Legal Consultation</h3>
          <form onSubmit={formik.handleSubmit} className="space-y-5">
            
            {/* First & Last Name */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full">
                <label htmlFor="firstName" className="text-gray-700 block text-sm font-medium mb-1">
                  First Name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="First Name"
                  className="border border-gray-300 rounded-md p-3 w-full"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                />
                {formik.touched.firstName && formik.errors.firstName && (
                  <p className="text-emerald-500 text-sm">{formik.errors.firstName}</p>
                )}
              </div>
              <div className="w-full">
                <label htmlFor="lastName" className="text-gray-700 block text-sm font-medium mb-1">
                  Last Name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Last Name"
                  className="border border-gray-300 rounded-md p-3 w-full"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName}
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <p className="text-emerald-500 text-sm">{formik.errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="text-gray-700 block text-sm font-medium mb-1">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="your@email.com"
                className="border border-gray-300 rounded-md p-3 w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
              />
              {formik.touched.email && formik.errors.email && (
                <p className="text-emerald-500 text-sm">{formik.errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="text-gray-700 block text-sm font-medium mb-1">
                Phone
              </label>
              <input
                id="phone"
                name="phone"
                type="text"
                placeholder="(555) 123-4567"
                className="border border-gray-300 rounded-md p-3 w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.phone}
              />
              {formik.touched.phone && formik.errors.phone && (
                <p className="text-emerald-500 text-sm">{formik.errors.phone}</p>
              )}
            </div>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="text-gray-700 block text-sm font-medium mb-1">
                Subject
              </label>
              <input
                id="subject"
                name="subject"
                type="text"
                placeholder="Brief subject of your legal inquiry"
                className="border border-gray-300 rounded-md p-3 w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.subject}
              />
              {formik.touched.subject && formik.errors.subject && (
                <p className="text-emerald-500 text-sm">{formik.errors.subject}</p>
              )}
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="text-gray-700 block text-sm font-medium mb-1">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows="4"
                placeholder="Describe your legal concern or question"
                className="border border-gray-300 rounded-md p-3 w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.message}
              ></textarea>
              {formik.touched.message && formik.errors.message && (
                <p className="text-emerald-500 text-sm">{formik.errors.message}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="w-full bg-emerald-500 text-white font-semibold py-3 rounded-md"
            >
              Submit Inquiry
            </button>
          </form>
        </div>
      </div>
      {showAlert && (
        <AlertModal
          message={alertMessage}
          onClose={() => setShowAlert(false)}
        />
      )}
    </div>
  );
};

export default ContactCard;
